// RelatorioProducaoOrdem.js - Relatorio de producao por Ordem de Producao
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Grid, Alert, CircularProgress,
  TablePagination, Autocomplete, MenuItem, Select, InputLabel, FormControl
} from '@mui/material';
import {
  Refresh, FilterList, Print
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const RelatorioProducaoOrdem = () => {
  const { api } = useAuth();

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [tiposFormula, setTiposFormula] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);

  // Estados de filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    setor: '',
    funcionario: '',
    tipoFormula: ''
  });
  const [opcoesFuncionarios, setOpcoesFuncionarios] = useState([]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  const [setoresOrdem, setSetoresOrdem] = useState([]);

  // Paginacao
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Cores por tipo de formula
  const coresTipo = {
    'Capsula': { bg: '#e3f2fd', color: '#1565c0', header: '#1976d2' },
    'Creme': { bg: '#fce4ec', color: '#c62828', header: '#e91e63' },
    'Outras': { bg: '#fff3e0', color: '#e65100', header: '#ff9800' },
    'Unidades': { bg: '#e8f5e9', color: '#2e7d32', header: '#4caf50' }
  };

  const obterCorTipo = (tipo) => coresTipo[tipo] || { bg: '#f5f5f5', color: '#616161', header: '#9e9e9e' };

  // Carregar opcoes de filtro
  const carregarOpcoesFiltro = async () => {
    try {
      const resFuncionarios = await api.listarFuncionarios();
      if (resFuncionarios.success) {
        setOpcoesFuncionarios(resFuncionarios.dados || []);
      }
    } catch (err) {
      // Erro silencioso
    }
  };

  // Carregar dados do relatorio
  const carregarDados = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o periodo (data inicio e fim)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      };

      if (funcionarioSelecionado) parametros.funcionario = funcionarioSelecionado.codigo;
      if (filtros.setor) parametros.setor = filtros.setor;
      if (filtros.tipoFormula) parametros.tipoFormula = filtros.tipoFormula;

      const res = await api.relatorioProducaoOrdem(parametros);

      if (res.success) {
        const dados = res.dados;
        setFuncionarios(dados.funcionarios || []);
        setTiposFormula(dados.tipos_formula || []);
        setSetoresOrdem(dados.setores_ordem || []);
        setEstatisticas(dados.estatisticas || null);
        setPage(0);
      } else {
        setError(res.message || 'Erro ao carregar dados');
      }
    } catch (err) {
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Definir periodo padrao (mes atual)
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));

    carregarOpcoesFiltro();
  }, []);

  const dadosPaginados = funcionarios.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calcular totais por tipo (para linha de total)
  const totaisPorTipo = {};
  tiposFormula.forEach(tipo => {
    totaisPorTipo[tipo] = funcionarios.reduce((sum, f) => sum + (f.tipos[tipo] || 0), 0);
  });
  const totalGeral = funcionarios.reduce((sum, f) => sum + f.total_geral, 0);
  const totalRequisicoes = funcionarios.reduce((sum, f) => sum + (f.total_requisicoes || 0), 0);

  return (
    <Box sx={{
      p: 3,
      '@media print': {
        '& .no-print': { display: 'none !important' },
        '& .MuiTableContainer-root': { maxHeight: 'none !important' },
        '& .MuiTable-root': { fontSize: '10px !important' },
        '& .MuiTableCell-root': { padding: '4px !important' }
      }
    }}>

      <Typography variant="h4" gutterBottom>
        Relatorio de Producao - Ordem de Producao
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Inicio"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={opcoesFuncionarios}
              getOptionLabel={(option) => option.nome}
              value={funcionarioSelecionado}
              onChange={(event, newValue) => setFuncionarioSelecionado(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Funcionario" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Setor</InputLabel>
              <Select
                value={filtros.setor}
                label="Setor"
                onChange={(e) => setFiltros(prev => ({ ...prev, setor: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {setoresOrdem.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tipo Formula</InputLabel>
              <Select
                value={filtros.tipoFormula}
                label="Tipo Formula"
                onChange={(e) => setFiltros(prev => ({ ...prev, tipoFormula: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Capsula">Capsula</MenuItem>
                <MenuItem value="Creme">Creme</MenuItem>
                <MenuItem value="Outras">Outras</MenuItem>
                <MenuItem value="Unidades">Unidades</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={carregarDados}
                disabled={loading}
                sx={{ bgcolor: '#7b1fa2' }}
              >
                Gerar Relatorio
              </Button>

              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={() => window.print()}
                disabled={funcionarios.length === 0}
              >
                Imprimir
              </Button>

              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                Limpar Filtros
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatisticas resumo */}
      {estatisticas && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {estatisticas.total_funcionarios}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Funcionarios
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" sx={{ color: '#7b1fa2' }}>
                  {estatisticas.total_geral?.toLocaleString('pt-BR') || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Producao Total
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {estatisticas.total_registros?.toLocaleString('pt-BR') || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Registros
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {estatisticas.media_por_funcionario?.toLocaleString('pt-BR') || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Media por Funcionario
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Totais por tipo */}
          {estatisticas.totais_por_tipo && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {Object.entries(estatisticas.totais_por_tipo).map(([tipo, total]) => {
                const cor = obterCorTipo(tipo);
                return (
                  <Grid item xs={6} sm={3} key={tipo}>
                    <Box textAlign="center" sx={{ bgcolor: cor.bg, borderRadius: 1, p: 1 }}>
                      <Typography variant="h6" sx={{ color: cor.color }}>
                        {total.toLocaleString('pt-BR')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: cor.color }}>
                        {tipo}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabela Principal */}
      {funcionarios.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f3e5f5',
                      minWidth: '250px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Funcionario
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f3e5f5',
                      minWidth: '120px',
                      position: 'sticky',
                      left: '250px',
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Setor
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e8eaf6',
                      color: '#283593',
                      minWidth: '110px',
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Requisições
                  </TableCell>

                  {tiposFormula.map(tipo => {
                    const cor = obterCorTipo(tipo);
                    return (
                      <TableCell
                        key={tipo}
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: cor.bg,
                          color: cor.color,
                          minWidth: '120px',
                          borderLeft: '1px solid #e0e0e0'
                        }}
                      >
                        {tipo}
                      </TableCell>
                    );
                  })}

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#7b1fa2',
                      color: 'white',
                      minWidth: '120px',
                      borderLeft: '2px solid #e0e0e0'
                    }}
                  >
                    Total Geral
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {dadosPaginados.map((func, index) => (
                  <TableRow
                    key={func.codigo_funcionario}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '250px'
                      }}
                    >
                      {func.funcionario_nome}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: '250px',
                        zIndex: 2,
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '120px',
                        fontSize: '0.8rem'
                      }}
                    >
                      {func.setor}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRight: '2px solid #e0e0e0',
                        fontWeight: 'bold',
                        color: '#283593',
                        backgroundColor: func.total_requisicoes > 0 ? '#e8eaf6' : 'transparent',
                        fontSize: '0.95rem'
                      }}
                    >
                      {func.total_requisicoes > 0 ? func.total_requisicoes.toLocaleString('pt-BR') : '-'}
                    </TableCell>

                    {tiposFormula.map(tipo => {
                      const valor = func.tipos[tipo] || 0;
                      const cor = obterCorTipo(tipo);
                      return (
                        <TableCell
                          key={tipo}
                          align="center"
                          sx={{
                            border: '1px solid #e0e0e0',
                            fontWeight: valor > 0 ? 'bold' : 'normal',
                            color: valor > 0 ? cor.color : '#bdbdbd',
                            backgroundColor: valor > 0 ? cor.bg : 'transparent',
                            fontSize: '0.95rem'
                          }}
                        >
                          {valor > 0 ? valor.toLocaleString('pt-BR') : '-'}
                        </TableCell>
                      );
                    })}

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                        border: '1px solid #e0e0e0',
                        borderLeft: '2px solid #e0e0e0',
                        fontSize: '1rem'
                      }}
                    >
                      {func.total_geral.toLocaleString('pt-BR')}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linha de totais */}
                <TableRow sx={{ backgroundColor: '#f3e5f5' }}>
                  <TableCell
                    colSpan={2}
                    sx={{
                      fontWeight: 'bold',
                      border: '1px solid #e0e0e0',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: '#f3e5f5'
                    }}
                  >
                    <Typography variant="h6">TOTAL</Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e8eaf6',
                      color: '#283593',
                      border: '1px solid #e0e0e0',
                      borderRight: '2px solid #e0e0e0',
                      fontSize: '1.1rem'
                    }}
                  >
                    {totalRequisicoes.toLocaleString('pt-BR')}
                  </TableCell>

                  {tiposFormula.map(tipo => {
                    const cor = obterCorTipo(tipo);
                    return (
                      <TableCell
                        key={tipo}
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: cor.bg,
                          color: cor.color,
                          border: '1px solid #e0e0e0',
                          fontSize: '1.1rem'
                        }}
                      >
                        {(totaisPorTipo[tipo] || 0).toLocaleString('pt-BR')}
                      </TableCell>
                    );
                  })}

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#7b1fa2',
                      color: 'white',
                      border: '1px solid #e0e0e0',
                      fontSize: '1.2rem'
                    }}
                  >
                    {totalGeral.toLocaleString('pt-BR')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginacao */}
          <TablePagination
            className="no-print"
            component="div"
            count={funcionarios.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por pagina:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioProducaoOrdem;
